import React, { useState, useEffect } from 'react';
import { userService, authService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { DatePicker } from '../components/ui/date-picker';
import AppLayout from '../layouts/AppLayout';

const ProfilePage = () => {
  const { user, updateUserData } = useAuth();
  
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    email: '',
    department: '',
    birthdate: null,
    joinDate: null,
    leave_balance: 0,
    sick_leave_balance: 0  // Added sick_leave_balance to state
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  
  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        
        // Use the /auth/me endpoint instead of /users/:id
        const response = await authService.getCurrentUser(`?_t=${timestamp}`);
        const userData = response.user;
        
        console.log('User data from API:', userData);
        
        setPersonalInfo({
          name: userData.name || '',
          email: userData.email || '',
          department: userData.department || '',
          birthdate: userData.birthdate ? new Date(userData.birthdate) : null,
          joinDate: userData.join_date ? new Date(userData.join_date) : null,
          leave_balance: userData.leave_balance || 0,
          sick_leave_balance: userData.sick_leave_balance || 0  // Store sick_leave_balance in state
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setProfileError('Failed to load your profile information');
        setLoading(false);
      }
    };
    
    if (user) {
      fetchUserData();
    }
  }, [user]);
  
  // Handle personal info change
  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle date change
  const handleDateChange = (name, date) => {
    setPersonalInfo(prev => ({
      ...prev,
      [name]: date
    }));
  };
  
  // Handle password change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Check if user can edit profile (admin or manager)
  const canEditProfile = () => {
    return user && (user.role === 'admin' || user.role === 'manager');
  };
  
  // Update personal info
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    
    // Only allow admins and managers to update profile
    if (!canEditProfile()) {
      setProfileError('You do not have permission to edit profile information');
      return;
    }
    
    try {
      setUpdatingProfile(true);
      
      // Format dates
      const userData = {
        name: personalInfo.name,
        email: personalInfo.email,
        department: personalInfo.department,
        birthdate: personalInfo.birthdate 
          ? personalInfo.birthdate.toISOString().split('T')[0] 
          : null,
        joinDate: personalInfo.joinDate 
          ? personalInfo.joinDate.toISOString().split('T')[0] 
          : null
      };
      
      const response = await userService.updateUser(user.id, userData);
      
      // Update auth context
      updateUserData({
        ...user,
        name: response.user.name,
        email: response.user.email,
        leave_balance: response.user.leave_balance,
        sick_leave_balance: response.user.sick_leave_balance // Make sure to update sick_leave_balance in context
      });
      
      // Update local state as well
      setPersonalInfo(prev => ({
        ...prev,
        leave_balance: response.user.leave_balance || prev.leave_balance,
        sick_leave_balance: response.user.sick_leave_balance || prev.sick_leave_balance
      }));
      
      setProfileSuccess('Profile updated successfully');
      setUpdatingProfile(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileError(err.response?.data?.message || 'Failed to update profile');
      setUpdatingProfile(false);
    }
  };
  
  // Update password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    // Validate password length
    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }
    
    try {
      setUpdatingPassword(true);
      
      await userService.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      // Reset password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setPasswordSuccess('Password updated successfully');
      setUpdatingPassword(false);
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError(err.response?.data?.message || 'Failed to update password');
      setUpdatingPassword(false);
    }
  };
  
  // Format date
  const formatDate = (date) => {
    if (!date) return 'Not set';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString(undefined, options);
  };
  
  if (loading) {
    return (
      <AppLayout>
        <div className="text-center py-10">Loading profile...</div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold">My Profile</h1>
        
        {/* Personal Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Personal Information</CardTitle>
          </CardHeader>
          
          <CardContent>
            {profileError && (
              <div className="bg-red-50 text-red-500 p-3 rounded-md mb-4">
                {profileError}
              </div>
            )}
            
            {profileSuccess && (
              <div className="bg-green-50 text-green-700 p-3 rounded-md mb-4">
                {profileSuccess}
              </div>
            )}
            
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="name">
                  Full Name
                </label>
                <Input
                  id="name"
                  name="name"
                  value={personalInfo.name}
                  onChange={handlePersonalInfoChange}
                  disabled={updatingProfile || !canEditProfile()}
                  readOnly={!canEditProfile()}
                  required
                />
                {!canEditProfile() && (
                  <p className="text-xs text-gray-500">
                    Name can only be changed by an administrator or manager
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="email">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={personalInfo.email}
                  onChange={handlePersonalInfoChange}
                  disabled={updatingProfile || !canEditProfile()}
                  readOnly={!canEditProfile()}
                  required
                />
                {!canEditProfile() && (
                  <p className="text-xs text-gray-500">
                    Email can only be changed by an administrator or manager
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="department">
                  Department
                </label>
                <Input
                  id="department"
                  name="department"
                  value={personalInfo.department}
                  onChange={handlePersonalInfoChange}
                  disabled={updatingProfile || user.role !== 'admin'}
                  readOnly={user.role !== 'admin'}
                />
                {user.role !== 'admin' && (
                  <p className="text-xs text-gray-500">
                    Department can only be changed by an administrator
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Birth Date</label>
                  <DatePicker
                    date={personalInfo.birthdate}
                    setDate={(date) => handleDateChange('birthdate', date)}
                    disabled={updatingProfile || !canEditProfile()}
                    placeholder="Select birth date"
                  />
                  {!canEditProfile() && (
                    <p className="text-xs text-gray-500">
                      Birth date can only be changed by an administrator or manager
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Join Date</label>
                  <DatePicker
                    date={personalInfo.joinDate}
                    setDate={(date) => handleDateChange('joinDate', date)}
                    disabled={updatingProfile || user.role !== 'admin'}
                    placeholder="Select join date"
                  />
                  {user.role !== 'admin' && (
                    <p className="text-xs text-gray-500">
                      Join date can only be changed by an administrator
                    </p>
                  )}
                </div>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium mb-2">Account Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Role</p>
                    <p className="font-medium capitalize">{user.role}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Annual Leave Balance</p>
                    <p className="font-medium">
                      {/* Use personal info state which is more fresh, fallback to user context or 0 */}
                      {personalInfo.leave_balance || user.leave_balance || user.leaveBalance || 0} days
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Join Date</p>
                    <p className="font-medium">{formatDate(personalInfo.joinDate)}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Sick Leave Balance</p>
                    <p className="font-medium">
                      {/* Use personal info state which is more fresh, fallback to user context or 0 */}
                      {personalInfo.sick_leave_balance || user.sick_leave_balance || user.sickLeaveBalance || 0} days
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </CardContent>
          
          <CardFooter>
            {canEditProfile() ? (
              <Button
                type="submit"
                onClick={handleUpdateProfile}
                disabled={updatingProfile}
              >
                {updatingProfile ? 'Updating...' : 'Update Profile'}
              </Button>
            ) : (
              <p className="text-sm text-gray-500">
                Only administrators and managers can update profile information.
              </p>
            )}
          </CardFooter>
        </Card>
        
        {/* Change Password Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Change Password</CardTitle>
          </CardHeader>
          
          <CardContent>
            {passwordError && (
              <div className="bg-red-50 text-red-500 p-3 rounded-md mb-4">
                {passwordError}
              </div>
            )}
            
            {passwordSuccess && (
              <div className="bg-green-50 text-green-700 p-3 rounded-md mb-4">
                {passwordSuccess}
              </div>
            )}
            
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="currentPassword">
                  Current Password
                </label>
                <Input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  disabled={updatingPassword}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="newPassword">
                  New Password
                </label>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  disabled={updatingPassword}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="confirmPassword">
                  Confirm New Password
                </label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  disabled={updatingPassword}
                  required
                />
              </div>
            </form>
          </CardContent>
          
          <CardFooter>
            <Button
              type="submit"
              onClick={handleUpdatePassword}
              disabled={updatingPassword}
            >
              {updatingPassword ? 'Updating...' : 'Change Password'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </AppLayout>
  );
};

export default ProfilePage;